/**
 * Camadas de nuvem a partir da sondagem — funções puras (testadas em
 * `nuvens.test.ts`).
 *
 * Método de Wang e Rossow (1995), o mais usado para radiossonda: um nível está
 * "em nuvem" quando a umidade relativa passa de 84% (em relação à água acima
 * de 0 °C e ao gelo abaixo), e uma camada só conta se em algum nível dela
 * chegar a 87%. Camadas separadas por menos de 300 m viram uma só.
 *
 * Camada de um nível só não conta: medido em 28/09/2026, na alta troposfera
 * elas pipocavam aos montes (umidade sobre gelo, onde a sonda é menos
 * precisa) — em Manaus eram 6 camadas, 3 delas de um nível.
 *
 * É uma estimativa: a sonda mede a umidade do ar, não a nuvem. As pontas
 * ficam nos níveis MEDIDOS, sem interpolar.
 */
import type { Nivel } from './types';

export const UR_NUVEM = 0.84;
export const UR_PICO = 0.87;
export const JUNTAR_M = 300;

/** Pressão de vapor de saturação sobre gelo (hPa), Magnus. */
export function saturacaoGelo(tC: number): number {
  return 6.112 * Math.exp((22.46 * tC) / (272.62 + tC));
}

/** Umidade relativa (0–1): em relação à água acima de 0 °C, ao gelo abaixo. */
export function umidadeRelativa(tC: number, dC: number): number {
  const e = 6.112 * Math.exp((17.67 * dC) / (dC + 243.5));
  const es = tC >= 0 ? 6.112 * Math.exp((17.67 * tC) / (tC + 243.5)) : saturacaoGelo(tC);
  return Math.min(1.2, e / es);
}

export interface CamadaDeNuvem {
  /** Pressão da base e do topo (hPa). */
  pBase: number;
  pTopo: number;
  /** Altura da base e do topo (m), medidas. */
  zBase: number;
  zTopo: number;
  /** Maior umidade relativa da camada (0–1). */
  urMax: number;
}

export function camadasDeNuvem(niveis: readonly Nivel[]): CamadaDeNuvem[] {
  const n = [...niveis]
    .filter((x) => x.t !== null && x.d !== null && x.p > 0 && Number.isFinite(x.z))
    .sort((a, b) => b.p - a.p);
  const brutas: CamadaDeNuvem[] = [];
  let atual: CamadaDeNuvem | null = null;
  for (const x of n) {
    const ur = umidadeRelativa(x.t!, x.d!);
    if (ur >= UR_NUVEM) {
      if (!atual) atual = { pBase: x.p, pTopo: x.p, zBase: x.z, zTopo: x.z, urMax: ur };
      else {
        atual.pTopo = x.p;
        atual.zTopo = x.z;
        atual.urMax = Math.max(atual.urMax, ur);
      }
    } else if (atual) {
      brutas.push(atual);
      atual = null;
    }
  }
  if (atual) brutas.push(atual);

  const out: CamadaDeNuvem[] = [];
  for (const c of brutas.filter((c) => c.urMax >= UR_PICO)) {
    const ant = out[out.length - 1];
    if (ant && c.zBase - ant.zTopo < JUNTAR_M) {
      ant.pTopo = c.pTopo;
      ant.zTopo = c.zTopo;
      ant.urMax = Math.max(ant.urMax, c.urMax);
    } else out.push({ ...c });
  }
  return out.filter((c) => c.pBase !== c.pTopo);
}

/** "0,9–2,1 km" (ou "1,2 km" quando base e topo arredondam igual). */
export function rotuloDaCamada(c: CamadaDeNuvem): string {
  const km = (z: number) => (z / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return km(c.zBase) === km(c.zTopo) ? `${km(c.zBase)} km` : `${km(c.zBase)}–${km(c.zTopo)} km`;
}
